import React, { PropsWithChildren, useCallback, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useCustomFormRecordById } from 'src/state/redux/selector/customFormRecord';
import { StandaloneForm, useGetFormConfigFromIndexDB } from 'src/state/redux/selector/crm';
import { useComposableForm } from 'src/components/CustomFields/ComposableForm';
import { CustomFormRecordORM } from 'src/types/orms';
import useSaveCustomFormRecord from './useSaveCustomFormRecord';
import { CustomFormRecordRelationshipModel, CustomFormRecordStatus } from '@alucio/aws-beacon-amplify/src/models';
import useCRMMeetingFormHandler from 'src/hooks/useCRMMeetingFormHandler/useCRMMeetingFormHandler';
import { CUSTOM_FORM_RECORD_ENTITY } from '@alucio/aws-beacon-amplify/src/API';
import { consolidateFormErrorMessages } from 'src/components/Meeting/Util';
import { drawerActions } from '../../redux/slice/drawer';
import { useCRMStatus } from 'src/hooks/useCRMStatus';

interface CustomFormRecordType {
  errorMessage?: string;
  isDirty?: boolean;
  isReadOnly: boolean;
  isSaving: boolean;
  recordORM?: CustomFormRecordORM;
  standaloneForm: StandaloneForm;
  onSave: (isSubmit?: boolean) => void;
}

const CustomFormRecordContext = React.createContext<CustomFormRecordType>({
  isSaving: false,
  isReadOnly: false,
  onSave: () => null,
  standaloneForm: {
    name: '',
    type: CUSTOM_FORM_RECORD_ENTITY.CRM_OBJECT,
    id: '',
  },
});

interface CustomFormRecordProviderProps {
  autoSubmitToCRM?: boolean;
  onSave?: (recordId: string) => void;
  parentId?: string;
  parentModel?: CustomFormRecordRelationshipModel;
  recordId?: string;
  standaloneForm: StandaloneForm;
}

const CustomFormRecordProvider: React.FC<PropsWithChildren<CustomFormRecordProviderProps>> = (props) => {
  const { autoSubmitToCRM, children, parentModel, parentId, recordId, standaloneForm } = props;
  const dispatch = useDispatch();
  const recordORM = useCustomFormRecordById(recordId);
  const { rhForm } = useComposableForm();
  const { isDirty } = rhForm.formState;
  const didAutoSubmitToCRM = useRef(false);
  const { canUseMeetingCRMFields } = useCRMStatus();
  const formSettings = useGetFormConfigFromIndexDB();
  const hasFormSettings = !!(
    formSettings.salesforceFormSettings.length ||
    formSettings.veevaVaultFormSettings.length
  );
  const savePayload = { recordId, parentId, parentModel, standaloneForm, onSave: props.onSave };
  const {
    errorMessage,
    isSaving,
    saveCustomForm,
    setErrorMessage,
    submitToCRMCustomForm,
  } = useSaveCustomFormRecord(savePayload);
  useCRMMeetingFormHandler({ rhForm });

  useEffect(() => {
    dispatch(drawerActions.setPendingChanges({ hasPendingChanges: isDirty }));
  }, [isDirty, dispatch]);

  const onSave = useCallback((isSubmit?: boolean): void => {
    rhForm.handleSubmit((values) => {
      isSubmit ? submitToCRMCustomForm({ values, recordORM })
        : saveCustomForm({ values, recordORM })
    }, (errors: { [s: string]: unknown; } | ArrayLike<unknown>) => {
      const formattedFormErrors = consolidateFormErrorMessages(errors);
      setErrorMessage(formattedFormErrors);
    })();
  }, [recordORM, rhForm, saveCustomForm, setErrorMessage, submitToCRMCustomForm]);

  useEffect(() => {
    const skipCRMAutoSubmit = !autoSubmitToCRM ||
      didAutoSubmitToCRM.current ||
      !recordORM?.model.id ||
      !canUseMeetingCRMFields ||
      !hasFormSettings;

    if (skipCRMAutoSubmit) {
      return;
    }

    didAutoSubmitToCRM.current = true;
    onSave(true);
  }, [autoSubmitToCRM, canUseMeetingCRMFields, hasFormSettings, onSave, recordORM?.model.id]);

  const value: CustomFormRecordType = {
    errorMessage,
    isDirty,
    isReadOnly: recordORM?.model.status === CustomFormRecordStatus.LOCKED,
    isSaving,
    recordORM,
    standaloneForm,
    onSave,
  };

  return (
    <CustomFormRecordContext.Provider value={value}>
      {children}
    </CustomFormRecordContext.Provider>
  );
};

export const useCustomFormRecordProvider = () => React.useContext(CustomFormRecordContext);

export default CustomFormRecordProvider;
